import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/** Which Crux emails the signed-in person gets about one brand report. */

type Path = paths["/api/workspaces/{workspaceId}/reports/{reportId}/email-preferences"];

export type EmailPreferences =
  Path["get"]["responses"][200]["content"]["application/json"];
export type EmailKind = EmailPreferences["kinds"][number]["kind"];

const onReport = (workspaceId: string, reportId: string) => ({
  path: { workspaceId, reportId },
});

export async function fetchEmailPreferences(
  workspaceId: string,
  reportId: string,
): Promise<EmailPreferences> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/email-preferences",
    { params: onReport(workspaceId, reportId) },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "load your email settings"));
  return data;
}

export async function setEmailPreference(
  workspaceId: string,
  reportId: string,
  change: { readonly kind: EmailKind; readonly enabled: boolean },
): Promise<EmailPreferences> {
  const { data, response } = await api.PUT(
    "/api/workspaces/{workspaceId}/reports/{reportId}/email-preferences",
    { params: onReport(workspaceId, reportId), body: change },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "save that email setting"));
  return data;
}

export type EmailHistory =
  paths["/api/workspaces/{workspaceId}/reports/{reportId}/email-history"]["get"]["responses"][200]["content"]["application/json"];
export type SentEmail = EmailHistory["emails"][number];

/** The emails the signed-in person was sent about one report, newest first. */
export async function fetchEmailHistory(
  workspaceId: string,
  reportId: string,
): Promise<EmailHistory> {
  const { data, response } = await api.GET(
    "/api/workspaces/{workspaceId}/reports/{reportId}/email-history",
    { params: onReport(workspaceId, reportId) },
  );
  if (data === undefined)
    throw new Error(problem(response.status, "load the emails sent to you"));
  return data;
}
